import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Box, Button, Grid, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Edit, Trash2, XSquare } from 'react-feather'
import dayjs, { Dayjs } from 'dayjs'
import { ModalLayout, CardAttribute } from '@/components'
import {
  ControlledDateField,
  ControlledSelect,
  ControlledTextField,
} from '@/components/inputs'
import { SelectOption } from '@/components'
import {
  useModal,
  useMutationWrapper,
  useDeleteInternalHoursLog,
  usePostInternalHoursLog,
  usePutInternalHoursLog,
} from '@/hooks'
import { InternalHoursLog } from '@/types'
import {
  DATE_FORMAT_DMY,
  DATE_FORMAT_DMY_TIME,
  DATE_FORMAT_YMD,
} from '@/constants'

const REPORT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'office', name: 'Office' },
  { value: 'leave', name: 'Leave' },
  { value: 'field_work', name: 'Field work' },
]

const LEAVE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'holiday', name: 'Holiday' },
  { value: 'sick_leave', name: 'Sick leave' },
  { value: 'menstrual_leave', name: 'Menstrual leave' },
  { value: 'mental_health_day', name: 'Mental health day' },
  { value: 'special_leave', name: 'Special leave' },
]

type InternalHoursLogForm = {
  date: Dayjs
  amount_hours: string
  report_type: string
  leave_type: string
  description: string
}

export type InternalHoursLogModalProps = {
  logData?: InternalHoursLog
}

export const InternalHoursLogModal = ({
  logData,
}: InternalHoursLogModalProps): JSX.Element => {
  const { t } = useTranslation('internalHours')
  const { close } = useModal()

  const isCreate = !logData
  const [data, setData] = useState<InternalHoursLog | undefined>(logData)
  const [isEdit, setIsEdit] = useState(isCreate)
  const [isConfirmDelete, setIsConfirmDelete] = useState(false)

  const schema = yup.object({
    date: yup.mixed<Dayjs>().required(),
    amount_hours: yup.string().required(),
    report_type: yup.string().required(),
    leave_type: yup.string().when('report_type', {
      is: 'leave',
      then: (s) => s.required(),
      otherwise: (s) => s.optional().nullable(),
    }),
    description: yup.string().optional().nullable(),
  })

  const defaultValues: InternalHoursLogForm = {
    date: data?.date ? dayjs(data.date) : dayjs(),
    amount_hours: data?.amount_hours ?? '',
    report_type: data?.report_type ?? '',
    leave_type: data?.leave_type ?? '',
    description: data?.description ?? '',
  }

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isValid },
  } = useForm<InternalHoursLogForm>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  })

  const { mutateAsync: createLog, isLoading: creating } = useMutationWrapper(
    () => usePostInternalHoursLog()
  )
  const { mutateAsync: updateLog, isLoading: updating } = useMutationWrapper(
    () => usePutInternalHoursLog(data?.id as number)
  )
  const { mutateAsync: deleteLog, isLoading: deleting } = useMutationWrapper(
    () => useDeleteInternalHoursLog(data?.id as number)
  )

  const isSaving = creating || updating
  const isLeave = watch('report_type') === 'leave'
  const isFieldWork = data?.report_type === 'field_work'

  const handleDelete = async () => {
    if (isConfirmDelete) {
      await deleteLog(undefined, {
        successMessageKey: 'internalHours:logs.notifications.deleted',
        onSuccess: () => close(),
      })
    } else {
      setIsConfirmDelete(true)
    }
  }

  const onSubmit = async (form: InternalHoursLogForm) => {
    const payload = {
      date: form.date.format(DATE_FORMAT_YMD),
      amount_hours: form.amount_hours,
      report_type: form.report_type,
      leave_type: form.report_type === 'leave' ? form.leave_type : null,
      is_paid_leave: form.report_type === 'leave' ? true : null,
      description: form.description || null,
    }

    if (isCreate) {
      await createLog(payload, {
        successMessageKey: 'internalHours:logs.notifications.created',
        onSuccess: () => close(),
      })
    } else {
      await updateLog(payload, {
        successMessageKey: 'internalHours:logs.notifications.updated',
        onSuccess: (res) => {
          setData(res)
          setIsEdit(false)
        },
      })
    }
  }

  const title = isCreate
    ? t('logs.createTitle')
    : t('logs.detailTitle', { id: data?.id })

  const footerActions = !isCreate ? (
    <Stack direction="row" justifyContent="space-between" width="100%">
      <Button onClick={() => close()}>{t('common:buttons.closeWindow')}</Button>
      <Button
        onClick={handleDelete}
        color="error"
        variant="text"
        disabled={deleting}
        endIcon={<Trash2 size={14} />}
      >
        {isConfirmDelete
          ? t('logs.buttons.confirmDelete')
          : t('logs.buttons.delete')}
      </Button>
    </Stack>
  ) : undefined

  return (
    <ModalLayout title={title} footerActions={footerActions}>
      <Stack spacing={4}>
        {!!data && (
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('logs.fieldTitles.createdAt')}
                value={
                  data.created_at
                    ? dayjs(data.created_at).format(DATE_FORMAT_DMY_TIME)
                    : '-'
                }
              />
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('logs.fieldTitles.updatedAt')}
                value={
                  data.updated_at
                    ? dayjs(data.updated_at).format(DATE_FORMAT_DMY_TIME)
                    : '-'
                }
              />
            </Grid>
          </Grid>
        )}

        {!isCreate && !isFieldWork && (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              onClick={() => setIsEdit((v) => !v)}
              variant="textThin"
              endIcon={isEdit ? <XSquare size={14} /> : <Edit size={14} />}
            >
              {isEdit ? t('logs.buttons.closeEditing') : t('logs.buttons.edit')}
            </Button>
          </Stack>
        )}

        {isEdit ? (
          <>
            <Stack spacing={4}>
              <Stack direction="row" spacing={4}>
                <Box>
                  <ControlledDateField<InternalHoursLogForm>
                    name="date"
                    control={control}
                    placeholder={t('logs.placeholders.date')}
                  />
                </Box>
                <Box>
                  <ControlledTextField<InternalHoursLogForm>
                    name="amount_hours"
                    control={control}
                    placeholder={t('logs.placeholders.hours')}
                  />
                </Box>
              </Stack>
              <Stack direction="row" spacing={4}>
                <ControlledSelect<InternalHoursLogForm>
                  name="report_type"
                  control={control}
                  placeholder={t('logs.placeholders.reportType')}
                  options={REPORT_TYPE_OPTIONS}
                  displayEmpty={true}
                />
                {isLeave && (
                  <ControlledSelect<InternalHoursLogForm>
                    name="leave_type"
                    control={control}
                    placeholder={t('logs.placeholders.leaveType')}
                    options={LEAVE_TYPE_OPTIONS}
                    displayEmpty={true}
                  />
                )}
              </Stack>
              <ControlledTextField<InternalHoursLogForm>
                name="description"
                control={control}
                multiline={true}
                minRows={3}
                placeholder={t('logs.placeholders.description')}
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end">
              <LoadingButton
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                loading={isSaving}
                disabled={!isValid || (!isCreate && !isDirty)}
              >
                {isCreate ? t('logs.buttons.create') : t('logs.buttons.save')}
              </LoadingButton>
            </Stack>
          </>
        ) : (
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('logs.fieldTitles.date')}
                value={
                  data?.date ? dayjs(data.date).format(DATE_FORMAT_DMY) : '-'
                }
              />
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('logs.fieldTitles.hours')}
                value={data?.amount_hours ?? '-'}
              />
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('logs.fieldTitles.reportType')}
                value={
                  REPORT_TYPE_OPTIONS.find((o) => o.value === data?.report_type)
                    ?.name ?? '-'
                }
              />
            </Grid>
            {data?.leave_type && (
              <Grid item={true} xs={12} md={6}>
                <CardAttribute
                  title={t('logs.fieldTitles.leaveType')}
                  value={
                    LEAVE_TYPE_OPTIONS.find((o) => o.value === data.leave_type)
                      ?.name ?? '-'
                  }
                />
              </Grid>
            )}
            <Grid item={true} xs={12}>
              <CardAttribute
                title={t('logs.fieldTitles.description')}
                value={data?.description || '-'}
              />
            </Grid>
          </Grid>
        )}
      </Stack>
    </ModalLayout>
  )
}
